import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import env from "@beam-australia/react-env";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useNavigate } from "react-router-dom";

import Layout from "../../../../layout/layout";
import ImageUpload from "../../../../components/fileUpload/fileUpload";
import useApi from "../../../../hooks/useApi";
import "./calculator.css";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { toastState } from "../../../../states/toastState";

const AddInfo = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useAtom(loadingState);

  const [toastConfig, setToastConfig] = useAtom(toastState);

  const [documentImage, setDocumentImage] = useState("");
  const [addressImage, setAddressImage] = useState("");
  const [paycheckImage1, setPaycheckImage1] = useState("");
  const [paycheckImage2, setPaycheckImage2] = useState("");
  const [paycheckImage3, setPaycheckImage3] = useState("");

  const [termsDialogVisible, setTermsDialogVisible] = useState(false);
  const [termsText, setTermsText] = useState("");

  // Carga el texto de términos y condiciones desde el archivo en /public
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/terminosCondiciones.txt`)
      .then((res) => res.text())
      .then((text) => setTermsText(text))
      .catch((e) => {
        console.log(e);
        setTermsText("");
      });
  }, []);

  // Valida las imágenes y abre el diálogo de términos
  const handleConfirmClick = () => {
    if (
      documentImage &&
      addressImage &&
      paycheckImage1 &&
      paycheckImage2 &&
      paycheckImage3
    ) {
      setTermsDialogVisible(true);
    } else {
      setToastConfig({
        severity: "error",
        summary: "Error",
        detail: "Todos los campos son requeridos",
        life: 3000,
        visible: true,
      });
    }
  };

  // Ejecuta el llamado a la API luego de aceptar los términos
  const acceptTermsAndConfirm = async () => {
    setTermsDialogVisible(false);
    setIsLoading(true);

    try {
      const data = {
        amount: state.amount,
        installmentsNumber: state.installmentNumber,
        beneficiaryDocumentImage: documentImage,
        beneficiaryAddressImage: addressImage,
        BeneficiarySalaryReceiptImage1: paycheckImage1,
        BeneficiarySalaryReceiptImage2: paycheckImage2,
        BeneficiarySalaryReceiptImage3: paycheckImage3,
      };

      const response = await api.post(
        `${env("API_URL")}/website/applications`,
        data
      );

      setToastConfig({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
        visible: true,
      });
      navigate("/summary", {
        state: response.data,
        replace: true,
      });
    } catch (e) {
      console.log(e);
      setToastConfig({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
        visible: true,
      });
    }

    setIsLoading(false);
  };

  // Se obtiene el nombre del mes seleccionado para la leyenda de los recibos
  // Si el día es menor o igual a 10 se pide uno menos
  const getMonthName = (previous = 0) => {
    const date = new Date();
    if (date.getDay <= 10) {
      previous--;
    }
    date.setMonth(date.getMonth() + previous);
    return date.toLocaleString("es-ES", { month: "long" });
  };

  const termsDialogFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <Button
        label="Cancelar"
        className="terms-cancel-button"
        onClick={() => setTermsDialogVisible(false)}
      />
      <Button
        label="Aceptar"
        className="terms-accept-button"
        onClick={acceptTermsAndConfirm}
        disabled={!termsText}
      />
    </div>
  );

  return (
    <React.Fragment>
      <Layout>
        <div className="calculator-container">
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <h2 style={{ marginBottom: 8 }}>Información requerida y confirmación</h2>
            <p style={{ margin: 0, color: "#2a3b4d" }}>
              Adjutá la información requerida y confirmá la solicitud.
            </p>
          </div>
          <div style={{ marginBottom: 24 }}>
            <div className="flex flex-column align-items-center" style={{ marginBottom: 20, width: "100%" }}>
              <div className="resultBox" style={{ marginBottom: 20 }}>
                <div className="flex" style={{ justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: "bold", paddingBottom: 10 }}>Cuotas</div>
                    <div style={{ fontSize: "1.5rem" }}>{state.installmentNumber}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: "bold", paddingBottom: 10 }}>Cuota mensual</div>
                    <div style={{ fontSize: "1.5rem" }}>{state.installmentAmount.toLocaleString("es-UY", { style: "currency", currency: "UYU" })}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: "bold", paddingBottom: 10 }}>Monto</div>
                    <div style={{ fontSize: "1.5rem" }}>{state.amount.toLocaleString("es-UY", { style: "currency", currency: "UYU" })}</div>
                  </div>
                </div>
              </div>
              <div style={{ width: "100%", maxWidth: 600 }}>
                <ImageUpload
                  setImage={setDocumentImage}
                  auto="true"
                  chooseLabel="Seleccionar imagen de documento"
                  emptyText="Arrastra y suelta la imagen de tu documento aquí"
                />
                <ImageUpload
                  setImage={setAddressImage}
                  auto="true"
                  chooseLabel="Seleccionar imagen de constancia de domicilio"
                  emptyText="Arrastra y suelta la imagen de tu constancia de domicilio aquí"
                />
                <ImageUpload
                  setImage={setPaycheckImage1}
                  auto="true"
                  chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(-3)}`}
                  emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(-3)} aquí`}
                />
                <ImageUpload
                  setImage={setPaycheckImage2}
                  auto="true"
                  chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(-2)}`}
                  emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(-2)} aquí`}
                />
                <ImageUpload
                  setImage={setPaycheckImage3}
                  auto="true"
                  chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(-1)}`}
                  emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(-1)} aquí`}
                />
                <div style={{ width: "100%" }}>
                  <Button
                    onClick={handleConfirmClick}
                    label={isLoading ? "Comfirmando..." : "Confirmar solicitud"}
                    className="button_colorSecuritas"
                    disabled={isLoading || !documentImage || !addressImage || !paycheckImage1 || !paycheckImage2 || !paycheckImage3}
                    loading={isLoading}
                    style={{ width: "100%", marginTop: "10px" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>

      <Dialog
        header="Términos y condiciones"
        visible={termsDialogVisible}
        style={{ width: "90vw", maxWidth: 600 }}
        footer={termsDialogFooter}
        onHide={() => setTermsDialogVisible(false)}
      >
        <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{termsText}</p>
      </Dialog>
    </React.Fragment>
  );
};

export default AddInfo;
